import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, fileUrl, mapEmbedUrl, isValidTrPhone, PHONE_HINT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Upload, Copy, Check, Image as ImageIcon, Palette, Share2, MessageCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { THEME_PRESETS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["business"], queryFn: () => api.get("/business").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", phone: "", address: "", theme_color: "#4F46E5", maps_url: "", notification_email: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileRef = useRef();
  const coverRef = useRef();
  const { user } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const changePw = async () => {
    if (pw.next.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
    if (pw.next !== pw.confirm) { toast.error("Yeni şifreler eşleşmiyor"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Şifreniz güncellendi");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPwSaving(false); }
  };

  useEffect(() => {
    if (data) {
      setForm({ name: data.name || "", phone: data.phone || "", address: data.address || "", theme_color: data.theme_color || "#4F46E5", maps_url: data.maps_url || "", notification_email: data.notification_email || "" });
      setLogoUrl(data.logo_url || "");
      setCoverUrl(data.cover_url || "");
    }
  }, [data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (form.phone && !isValidTrPhone(form.phone)) {
      toast.error(`Telefon numarası 11 rakam olmalı. ${PHONE_HINT}`);
      return;
    }
    setSaving(true);
    try {
      await api.put("/business", form);
      qc.invalidateQueries({ queryKey: ["business"] });
      toast.success("Bilgiler kaydedildi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(res.logo_url);
      qc.invalidateQueries({ queryKey: ["business"] });
      toast.success("Logo yüklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/business/cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverUrl(res.cover_url);
      qc.invalidateQueries({ queryKey: ["business"] });
      toast.success("Kapak görseli yüklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingCover(false); }
  };

  if (isLoading || !data)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const bookingUrl = `${window.location.origin}/r/${data.slug}`;
  const shareMsg = `${data.name} - Online randevu almak için tıklayın: ${bookingUrl}`;
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg)}`, "_blank", "noopener");
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: data.name, text: `${data.name} - Online randevu`, url: bookingUrl }); }
      catch (e) { /* user cancelled */ }
    } else {
      copy();
      toast.info("Tarayıcınız paylaşımı desteklemiyor, link kopyalandı");
    }
  };
  const copy = () => {
    const fallback = () => window.prompt("Rezervasyon linkinizi kopyalayın:", bookingUrl);
    try {
      const p = navigator.clipboard?.writeText(bookingUrl);
      if (p && typeof p.then === "function")
        p.then(() => { setCopied(true); toast.success("Kopyalandı"); setTimeout(() => setCopied(false), 2000); }).catch(fallback);
      else { setCopied(true); toast.success("Kopyalandı"); setTimeout(() => setCopied(false), 2000); }
    } catch (e) { fallback(); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold">Ayarlar</h1>
        <p className="text-muted-foreground mt-1">İşletme bilgilerinizi düzenleyin</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl border border-border bg-secondary overflow-hidden flex items-center justify-center shrink-0">
            {logoUrl ? <img src={fileUrl(logoUrl)} alt="logo" className="w-full h-full object-cover" data-testid="logo-preview" />
              : <span className="text-xs text-muted-foreground text-center px-2">Logo yok</span>}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-lg" data-testid="upload-logo-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Logo Yükle
            </Button>
            <p className="text-xs text-muted-foreground mt-1.5">PNG, JPG veya WEBP</p>
          </div>
        </div>

        <div><Label>İşletme Adı</Label><Input value={form.name} onChange={set("name")} className="h-11 mt-1.5" data-testid="settings-name-input" /></div>
        <div>
          <Label>Telefon</Label>
          <Input value={form.phone} onChange={set("phone")} className="h-11 mt-1.5" placeholder="0 5xx xxx xx xx" data-testid="settings-phone-input" />
          {form.phone && !isValidTrPhone(form.phone)
            ? <p className="text-xs text-red-600 mt-1">Telefon numarası 11 rakam olmalı. {PHONE_HINT}</p>
            : <p className="text-xs text-muted-foreground mt-1">{PHONE_HINT}</p>}
        </div>
        <div><Label>Adres</Label><Textarea value={form.address} onChange={set("address")} className="mt-1.5" data-testid="settings-address-input" /></div>

        <div>
          <Label>Bildirim E-postası</Label>
          <Input value={form.notification_email} onChange={set("notification_email")} type="email" className="h-11 mt-1.5" placeholder="Boş bırakırsanız hesap e-postanız kullanılır" data-testid="settings-notif-email-input" />
          <p className="text-xs text-muted-foreground mt-1">Yeni rezervasyon, iptal ve erteleme bildirimleri bu adrese gönderilir.</p>
        </div>

        {form.address && form.address.trim() && (
          <div>
            <Label className="text-muted-foreground">Harita Önizleme (adresten otomatik)</Label>
            <div className="mt-1.5 rounded-xl overflow-hidden border border-border">
              <iframe title="harita-onizleme" src={mapEmbedUrl(form.address)} className="w-full h-52 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="settings-map-preview" />
            </div>
          </div>
        )}
        <div>
          <Label>Google Harita Linki (opsiyonel)</Label>
          <Input value={form.maps_url} onChange={set("maps_url")} className="h-11 mt-1.5" placeholder="https://maps.app.goo.gl/..." data-testid="settings-maps-input" />
          <p className="text-xs text-muted-foreground mt-1">Boş bırakırsanız yol tarifi otomatik olarak adresinizden oluşturulur.</p>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Kaydet</>}
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="flex items-center gap-2 text-primary">
          <Palette className="w-5 h-5" />
          <h2 className="font-semibold">Rezervasyon Sayfası Teması</h2>
        </div>

        <div>
          <Label>Tema Rengi</Label>
          <div className="flex flex-wrap items-center gap-2.5 mt-2">
            {THEME_PRESETS.map((p) => (
              <button key={p.color} type="button" title={p.name}
                onClick={() => setForm((f) => ({ ...f, theme_color: p.color }))}
                className={cn("w-9 h-9 rounded-full border-2 transition-transform hover:scale-110",
                  form.theme_color?.toLowerCase() === p.color.toLowerCase() ? "border-foreground ring-2 ring-offset-2 ring-foreground/20" : "border-transparent")}
                style={{ backgroundColor: p.color }} data-testid={`theme-color-${p.color.replace('#','')}`} />
            ))}
            <label className="flex items-center gap-2 ml-1 cursor-pointer">
              <input type="color" value={form.theme_color} onChange={(e) => setForm((f) => ({ ...f, theme_color: e.target.value }))}
                className="w-9 h-9 rounded-full border border-border cursor-pointer bg-transparent" data-testid="theme-color-custom" />
              <span className="text-xs text-muted-foreground">Özel</span>
            </label>
          </div>
        </div>

        <div>
          <Label>Kapak Görseli</Label>
          <div className="mt-2 rounded-xl border border-border overflow-hidden bg-secondary aspect-[16/6] flex items-center justify-center relative">
            {coverUrl
              ? <img src={fileUrl(coverUrl)} alt="kapak" className="w-full h-full object-cover" data-testid="cover-preview" />
              : <span className="text-sm text-muted-foreground">Kapak görseli yok</span>}
            <div className="absolute inset-0" style={{ background: coverUrl ? `linear-gradient(0deg, ${form.theme_color}22, transparent)` : "none" }} />
          </div>
          <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="cover-file-input" />
          <Button variant="outline" onClick={() => coverRef.current?.click()} disabled={uploadingCover} className="rounded-lg mt-3" data-testid="upload-cover-btn">
            {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImageIcon className="w-4 h-4 mr-1" />}
            Kapak Yükle
          </Button>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-theme-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Temayı Kaydet</>}
        </Button>
      </div>

      {user?.has_password && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-5" data-testid="password-card">
          <div className="flex items-center gap-2 text-primary">
            <Lock className="w-5 h-5" />
            <h2 className="font-semibold">Şifre Değiştir</h2>
          </div>
          <div><Label>Mevcut Şifre</Label><Input type="password" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} className="h-11 mt-1.5" data-testid="pw-current-input" /></div>
          <div><Label>Yeni Şifre</Label><Input type="password" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} className="h-11 mt-1.5" placeholder="En az 6 karakter" data-testid="pw-new-input" /></div>
          <div><Label>Yeni Şifre (Tekrar)</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} className="h-11 mt-1.5" data-testid="pw-confirm-input" /></div>
          <Button onClick={changePw} disabled={pwSaving || !pw.current || !pw.next} className="rounded-lg" data-testid="change-password-btn">
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1" /> Şifreyi Güncelle</>}
          </Button>
        </div>
      )}

      <div className="rounded-xl border border-border bg-accent/50 p-5 space-y-3">
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Rezervasyon Linkiniz</p>
        <div className="flex items-stretch rounded-lg border border-border bg-secondary/60 overflow-hidden">
          <span className="px-3 text-sm text-muted-foreground bg-secondary flex items-center whitespace-nowrap">/r/</span>
          <span className="flex-1 h-11 px-2 text-sm bg-transparent flex items-center min-w-0 truncate font-mono text-foreground/80" data-testid="settings-slug-readonly">{data.slug}</span>
        </div>
        <p className="text-xs text-muted-foreground">Rezervasyon adresiniz sabittir ve değiştirilemez.</p>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={copy} className="rounded-lg" data-testid="settings-copy-link-btn">
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Kopyala
          </Button>
        </div>
        <p className="font-mono text-xs text-muted-foreground break-all">{bookingUrl}</p>

        <div className="pt-1">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Linkinizi paylaşın ve yeni müşteriler kazanın</p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={shareWhatsApp} className="rounded-lg bg-[#25D366] hover:bg-[#1fb457] text-white" data-testid="share-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp'ta Paylaş
            </Button>
            <Button size="sm" variant="outline" onClick={nativeShare} className="rounded-lg" data-testid="share-native-btn">
              <Share2 className="w-4 h-4 mr-1" /> Diğer (Instagram, SMS…)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
